import type { PrintData } from './printService';

/**
 * Every document this church prints, as the administrator thinks of it.
 *
 * This is the one list Settings shows and the one thing a screen names when it
 * prints. A document is its own mapping: "Baptism Certificate" and "First
 * Communion Certificate" are separate entries with separate layouts, rather
 * than two things sharing one "Certificate" setting.
 *
 * `key` is what reaches the backend as the template's document type. It is an
 * internal value and never shown to an administrator, who sees `label`.
 * `category` only groups the list on screen; it carries no behaviour.
 */

export type DocumentCategory =
  | 'Certificates'
  | 'Records'
  | 'Receipts'
  | 'Financial'
  | 'Registers';

export interface PrintDocumentDef {
  /** Stored as the template's document type. Internal. */
  key: string;
  /** What the administrator sees, e.g. "Baptism Certificate". */
  label: string;
  /** Module id as the registry spells it. */
  module: string;
  category: DocumentCategory;
  /** One line under the name in the list. */
  describes: string;
  /** Where in the application this document is produced. */
  producedAt: string;
  /** Several layouts make sense for this document, so printing offers a choice. */
  selectable?: boolean;
  /**
   * Prints with a built-in layout until the parish configures its own, rather
   * than refusing. Used where the paper is a working list, not an official form.
   */
  builtIn?: boolean;
  /**
   * How many columns a starting layout prints. Seven fits most registers on a
   * portrait sheet; a register whose every column matters asks for more.
   */
  listColumns?: number;
  /** A starting layout prints landscape: the register is wider than a portrait sheet. */
  landscape?: boolean;
  /**
   * Fields this document is not complete without. A saved layout written
   * before one of them existed does not gain it by itself, so Settings names
   * what such a layout is missing.
   */
  mustPrint?: string[];
}

export const PRINT_DOCUMENTS: PrintDocumentDef[] = [
  // --- Certificates -------------------------------------------------------
  {
    key: 'BAPTISM_CERTIFICATE', label: 'Baptism Certificate',
    module: 'Baptism', category: 'Certificates', selectable: true,
    describes: 'Issued after an infant or adult baptism.',
    producedAt: 'Baptism ▸ open a record ▸ Print'
  },
  {
    key: 'COMMUNION_CERTIFICATE', label: 'First Communion Certificate',
    module: 'Baptism', category: 'Certificates', selectable: true,
    describes: 'Issued after a first communion.',
    producedAt: 'Baptism ▸ First Communion ▸ open a record ▸ Print'
  },
  {
    key: 'CONFIRMATION_CERTIFICATE', label: 'Confirmation Certificate',
    module: 'Baptism', category: 'Certificates', selectable: true,
    describes: 'Issued after a confirmation.',
    producedAt: 'Baptism ▸ Confirmation ▸ open a record ▸ Print'
  },
  {
    key: 'MARRIAGE_CERTIFICATE', label: 'Marriage Certificate',
    module: 'Marriage', category: 'Certificates', selectable: true,
    describes: 'Issued after a marriage is solemnised.',
    producedAt: 'Marriage ▸ open a certificate ▸ Print'
  },
  {
    key: 'BANNS_NOTICE', label: 'Banns Notice',
    module: 'Marriage', category: 'Certificates',
    describes: 'The notice read out and posted before a marriage.',
    producedAt: 'Marriage ▸ Banns ▸ open a record ▸ Print'
  },

  // --- Records ------------------------------------------------------------
  {
    key: 'MEMBER_RECORD', label: 'Member Record',
    module: 'Members & Families', category: 'Records',
    describes: "One member's full details on a sheet.",
    producedAt: 'Members & Families ▸ Members ▸ open a member ▸ Print'
  },
  {
    key: 'FAMILY_RECORD', label: 'Family Record',
    module: 'Members & Families', category: 'Records',
    describes: 'A family with its head and every member.',
    producedAt: 'Members & Families ▸ Heads ▸ row menu ▸ Print'
  },
  {
    key: 'FAMILY_REGISTRATION', label: 'Family Registration Form',
    module: 'Members & Families', category: 'Records',
    describes: 'A family\'s public registration: its status, the head and every member as submitted.',
    producedAt: 'Public registration link ▸ View / Print, and Members & Families ▸ Pending Approval ▸ Print'
  },
  {
    key: 'MISSIONARY_PROFILE', label: 'Missionary Profile',
    module: 'Missionaries', category: 'Records',
    describes: "A missionary's details and deployment.",
    producedAt: 'Missionaries ▸ open a missionary ▸ Print'
  },
  {
    key: 'BURIAL_RECORD', label: 'Burial Record',
    module: 'Cemetery', category: 'Records',
    describes: 'The record of one burial and its plot.',
    producedAt: 'Cemetery ▸ Burials ▸ row ▸ Print'
  },
  {
    key: 'REPORT_CARD', label: 'Sunday School Report Card',
    module: 'Sunday School', category: 'Records', selectable: true, mustPrint: ['subject'],
    describes: "A student's exam results for the year.",
    producedAt: 'Sunday School ▸ Reports ▸ row ▸ Report card'
  },

  // --- Receipts -----------------------------------------------------------
  {
    key: 'OFFERING_RECEIPT', label: 'Offering Receipt',
    module: 'Offerings', category: 'Receipts', selectable: true,
    describes: 'Given to a donor for an offering or collection.',
    producedAt: 'Offerings ▸ open a receipt ▸ Print'
  },

  // --- Financial ----------------------------------------------------------
  // Accounts prints seven different statements, each with its own columns and
  // totals, so each is its own document. One shared "Financial Statement"
  // layout could only fit one of them and printed the rest empty.
  {
    key: 'ACCOUNTS_LEDGER', label: 'General Ledger',
    module: 'Accounts', category: 'Financial', builtIn: true, listColumns: 8,
    describes: 'Every voucher from collections, donations and expenses, with totals.',
    producedAt: 'Accounts ▸ General Ledger ▸ Print'
  },
  {
    key: 'FINANCIAL_SUMMARY', label: 'Financial Summary',
    module: 'Accounts', category: 'Financial', builtIn: true,
    describes: 'Income, expenditure, surplus and closing balances, with the head-wise breakdown.',
    producedAt: 'Accounts ▸ Reports ▸ Financial Summary Dashboard ▸ Print'
  },
  {
    key: 'CASH_BOOK', label: 'Cash Book',
    module: 'Accounts', category: 'Financial', builtIn: true, listColumns: 8,
    describes: 'Cash receipts and payments with the running balance, opening and closing.',
    producedAt: 'Accounts ▸ Reports ▸ Official Cash Book ▸ Print'
  },
  {
    key: 'BANK_BOOK', label: 'Bank Book',
    module: 'Accounts', category: 'Financial', builtIn: true, listColumns: 8,
    describes: 'Bank receipts and payments with the running balance, opening and closing.',
    producedAt: 'Accounts ▸ Reports ▸ Official Bank Book ▸ Print'
  },
  {
    key: 'TRIAL_BALANCE', label: 'Trial Balance',
    module: 'Accounts', category: 'Financial', builtIn: true,
    describes: 'Debit and credit by account head, with the grand totals.',
    producedAt: 'Accounts ▸ Reports ▸ Trial Balance ▸ Print'
  },
  {
    key: 'INCOME_EXPENDITURE', label: 'Income & Expenditure Statement',
    module: 'Accounts', category: 'Financial', builtIn: true,
    describes: 'Income and expenditure by head, and the surplus or deficit.',
    producedAt: 'Accounts ▸ Reports ▸ Income & Expenditure Statement ▸ Print'
  },
  {
    key: 'BALANCE_SHEET', label: 'Balance Sheet',
    module: 'Accounts', category: 'Financial', builtIn: true,
    describes: 'Funds against assets as on the period end.',
    producedAt: 'Accounts ▸ Reports ▸ Balance Sheet ▸ Print'
  },
  {
    key: 'ACCOUNTS_AUDIT_LOG', label: 'Internal Audit & Void Log',
    module: 'Accounts', category: 'Financial', builtIn: true, listColumns: 9,
    describes: 'Voided and deleted transactions, who asked and who approved.',
    producedAt: 'Accounts ▸ Reports ▸ Internal Audit & Void Log ▸ Print'
  },
  {
    key: 'BUDGET_REPORT', label: 'Budget vs Actual',
    module: 'Budget Management', category: 'Financial', builtIn: true, listColumns: 7,
    describes: 'Every budget line against its actual spend and variance.',
    producedAt: 'Budget ▸ Dashboard ▸ By line ▸ Print'
  },
  {
    key: 'BUDGET_MONTHLY_REPORT', label: 'Budget Spend by Month',
    module: 'Budget Management', category: 'Financial', builtIn: true, listColumns: 16, landscape: true,
    describes: 'What each head spent in each month of the financial year.',
    producedAt: 'Budget ▸ Dashboard ▸ By month ▸ Print'
  },
  {
    key: 'EXPENSE_LEDGER', label: 'Expense Ledger',
    module: 'Expenses', category: 'Financial', builtIn: true, listColumns: 10, landscape: true,
    describes: 'The expense register for the chosen filters, with its total.',
    producedAt: 'Expenses ▸ Ledger ▸ Print'
  },

  // --- Registers ----------------------------------------------------------
  {
    key: 'MEMBER_DIRECTORY', label: 'Member Directory',
    module: 'Members & Families', category: 'Registers',
    describes: 'The member list as it is filtered on screen.',
    producedAt: 'Members & Families ▸ Members ▸ Print'
  },
  {
    key: 'FAMILY_REGISTER', label: 'Family Register',
    module: 'Members & Families', category: 'Registers',
    describes: 'The family list as it is filtered on screen.',
    producedAt: 'Members & Families ▸ Heads ▸ Print'
  },
  {
    key: 'ATTENDANCE_REGISTER', label: 'Attendance Register',
    module: 'Attendance', category: 'Registers',
    describes: 'Service attendance for the chosen dates.',
    producedAt: 'Attendance ▸ Ledger ▸ Print'
  },
  {
    key: 'CEMETERY_REGISTER', label: 'Plot Register',
    module: 'Cemetery', category: 'Registers', builtIn: true, listColumns: 9,
    describes: 'Plots, their sections, availability and who they are allotted to.',
    producedAt: 'Cemetery ▸ Plot map / Plot creation / Booking ▸ Print'
  },
  {
    key: 'CEMETERY_BURIAL_REGISTER', label: 'Burial Register',
    module: 'Cemetery', category: 'Registers', builtIn: true, listColumns: 11, landscape: true,
    describes: 'Every burial with the deceased, plot and next of kin.',
    producedAt: 'Cemetery ▸ Burials ▸ Print'
  },
  {
    key: 'CEMETERY_MAINTENANCE_REGISTER', label: 'Cemetery Maintenance Log',
    module: 'Cemetery', category: 'Registers', builtIn: true, listColumns: 8,
    describes: 'Upkeep work carried out in the cemetery and its cost.',
    producedAt: 'Cemetery ▸ Maintenance ▸ Print'
  },
  {
    key: 'CHOIR_REGISTER', label: 'Choir Register — Members',
    module: 'Choir Management', category: 'Registers', builtIn: true, listColumns: 10,
    describes: 'Choir members with voice type, role and status.',
    producedAt: 'Choir ▸ Choir Members ▸ Print'
  },
  {
    key: 'CHOIR_SCHEDULE_REGISTER', label: 'Choir Register — Schedules',
    module: 'Choir Management', category: 'Registers', builtIn: true, listColumns: 10,
    describes: 'Practices and events with conductor, headcount and songs.',
    producedAt: 'Choir ▸ Schedules & Attendance ▸ Print'
  },
  {
    key: 'CHOIR_ASSET_REGISTER', label: 'Choir Register — Library & Assets',
    module: 'Choir Management', category: 'Registers', builtIn: true, listColumns: 9,
    describes: 'Music library and choir assets with who holds them.',
    producedAt: 'Choir ▸ Library & Assets ▸ Print'
  },
  // Each Assets tab stores different columns, so each has its own register: a
  // single shared layout printed blank cells on every tab but the one it was
  // written for.
  {
    key: 'ASSET_REGISTER', label: 'Asset Register',
    module: 'Asset Management', category: 'Registers', builtIn: true,
    describes: 'The asset registry or the write-off register.',
    producedAt: 'Assets ▸ Master Registry / Write-off Register ▸ Print'
  },
  {
    key: 'ASSET_ALLOCATION_REGISTER', label: 'Asset Allocation Register',
    module: 'Asset Management', category: 'Registers', builtIn: true,
    describes: 'Asset allocations and transfers between locations and custodians.',
    producedAt: 'Assets ▸ Allocation & Transfer ▸ Print'
  },
  {
    key: 'ASSET_MAINTENANCE_REGISTER', label: 'Asset Maintenance Log',
    module: 'Asset Management', category: 'Registers', builtIn: true,
    describes: 'Repairs and servicing carried out on church assets.',
    producedAt: 'Assets ▸ Maintenance Log ▸ Print'
  },
  {
    key: 'ASSET_TRANSFER_RECORD', label: 'Asset Transfer Slip',
    module: 'Asset Management', category: 'Records', builtIn: true,
    describes: 'One allocation or transfer: the asset, where it went and who received it.',
    producedAt: 'Assets ▸ Allocation & Transfer ▸ row ▸ Print'
  },
  {
    key: 'ASSET_MAINTENANCE_RECORD', label: 'Asset Maintenance Record',
    module: 'Asset Management', category: 'Records', builtIn: true,
    describes: 'One repair or service: the asset, the work done and its cost.',
    producedAt: 'Assets ▸ Maintenance Log ▸ row ▸ Print'
  },
  {
    key: 'EVENT_SCHEDULE', label: 'Event Schedule',
    module: 'Event Management', category: 'Registers',
    describes: 'Scheduled events for the chosen period.',
    producedAt: 'Events ▸ Directory ▸ Print'
  },
  {
    key: 'MINUTES_EXTRACT', label: 'Meeting Register',
    module: 'Meeting Management', category: 'Registers', builtIn: true, listColumns: 10,
    describes: 'Meetings with their committee, date, venue and status.',
    producedAt: 'Meetings ▸ Meetings ▸ Print'
  },
  {
    key: 'MINUTES_REGISTER', label: 'Minutes Book Extract',
    module: 'Meeting Management', category: 'Registers', builtIn: true, listColumns: 11, landscape: true,
    describes: 'Minutes and resolutions with their decisions.',
    producedAt: 'Meetings ▸ Minutes & Resolutions ▸ Print'
  },
  {
    key: 'ACTION_ITEMS_REGISTER', label: 'Action Items Register',
    module: 'Meeting Management', category: 'Registers', builtIn: true, listColumns: 9,
    describes: 'Action items, who they are assigned to and when they are due.',
    producedAt: 'Meetings ▸ Action Items ▸ Print'
  },
  {
    key: 'BAPTISM_REGISTER', label: 'Baptism Register',
    module: 'Baptism', category: 'Registers',
    describes: 'The baptism register itself, as filtered on screen.',
    producedAt: 'Baptism ▸ Baptisms ▸ Print'
  },
  {
    key: 'COMMUNION_REGISTER', label: 'First Communion Register',
    module: 'Baptism', category: 'Registers',
    describes: 'Everyone admitted to first communion, as filtered on screen.',
    producedAt: 'Baptism ▸ First Communion ▸ Print'
  },
  {
    key: 'CONFIRMATION_REGISTER', label: 'Confirmation Register',
    module: 'Baptism', category: 'Registers',
    describes: 'Everyone confirmed, as filtered on screen.',
    producedAt: 'Baptism ▸ Confirmation ▸ Print'
  },
  {
    key: 'MARRIAGE_REGISTER', label: 'Marriage Register',
    module: 'Marriage', category: 'Registers',
    describes: 'The marriage register itself, as filtered on screen.',
    producedAt: 'Marriage ▸ Certificates ▸ Print'
  },
  {
    key: 'BANNS_REGISTER', label: 'Banns Register',
    module: 'Marriage', category: 'Registers',
    describes: 'Banns published, with their three reading dates.',
    producedAt: 'Marriage ▸ Banns ▸ Print'
  },
  {
    key: 'SACRAMENT_STATUS', label: 'Sacrament Status List',
    module: 'Members & Families', category: 'Registers',
    describes: 'Who has been baptised, communed and confirmed \u2014 and who is due.',
    producedAt: 'Congregation ▸ Sacraments ▸ Print'
  },
  {
    key: 'SUNDAY_SCHOOL_ROSTER', label: 'Sunday School Class Register',
    module: 'Sunday School', category: 'Registers',
    describes: 'The students enrolled in one class, with attendance.',
    producedAt: 'Sunday School ▸ Classes ▸ open a class ▸ Print'
  },
  // Each Committee History tab stores different columns, so each has its own
  // register: one shared layout printed blank dates on the other tabs.
  {
    key: 'COMMITTEE_REGISTER', label: 'Committee Register — Electoral Terms',
    module: 'Committee History', category: 'Registers', builtIn: true, listColumns: 8,
    describes: 'Electoral terms with their dates, status and remarks.',
    producedAt: 'Committee History ▸ Electoral Terms ▸ Print'
  },
  {
    key: 'COMMITTEE_MEMBER_REGISTER', label: 'Committee Register — Members',
    module: 'Committee History', category: 'Registers', builtIn: true, listColumns: 10,
    describes: 'Committee members, their roles, terms, periods and portfolio notes.',
    producedAt: 'Committee History ▸ Committee Members ▸ Print'
  },
  {
    key: 'SECRETARY_HANDOVER_REGISTER', label: 'Committee Register — Secretary Handovers',
    module: 'Committee History', category: 'Registers', builtIn: true, listColumns: 9,
    describes: 'Secretary handovers with witness, status and the items handed over.',
    producedAt: 'Committee History ▸ Secretary Handover ▸ Print'
  },
  {
    key: 'COMMITTEE_MEMBER_RECORD', label: 'Committee Member Record',
    module: 'Committee History', category: 'Records', builtIn: true,
    describes: 'One committee member with every detail recorded.',
    producedAt: 'Committee History ▸ Committee Members ▸ row ▸ View ▸ Print'
  },
  {
    key: 'COMMITTEE_TERM_RECORD', label: 'Electoral Term Record',
    module: 'Committee History', category: 'Records', builtIn: true,
    describes: 'One electoral term with every detail recorded.',
    producedAt: 'Committee History ▸ Electoral Terms ▸ row ▸ View ▸ Print'
  },
  {
    key: 'SECRETARY_HANDOVER_RECORD', label: 'Secretary Handover Record',
    module: 'Committee History', category: 'Records', builtIn: true,
    describes: 'One secretary handover with its checklist.',
    producedAt: 'Committee History ▸ Secretary Handover ▸ row ▸ View ▸ Print'
  },
  {
    key: 'PASTOR_REGISTER', label: 'Pastor Turnover Register — Tenures',
    module: 'Pastor Turnover', category: 'Registers', builtIn: true, listColumns: 12, landscape: true,
    describes: 'Tenures of the presbyters who served, with their dates.',
    producedAt: 'Pastor Turnover ▸ Tenure History ▸ Print'
  },
  {
    key: 'PASTOR_POSTING_REGISTER', label: 'Pastor Turnover Register — Postings',
    module: 'Pastor Turnover', category: 'Registers', builtIn: true, listColumns: 10,
    describes: 'Postings and transfer orders.',
    producedAt: 'Pastor Turnover ▸ Postings & Orders ▸ Print'
  },
  {
    key: 'PASTOR_HANDOVER_REGISTER', label: 'Pastor Turnover Register — Handovers',
    module: 'Pastor Turnover', category: 'Registers', builtIn: true, listColumns: 9,
    describes: 'Handovers between presbyters with witness and approval.',
    producedAt: 'Pastor Turnover ▸ Handover Register ▸ Print'
  },
  // Each Pastoral Care tab stores different columns, so each has its own
  // register; one shared layout printed blank cells on every other tab.
  {
    key: 'PASTORAL_CARE_REGISTER', label: 'Pastoral Care Register — Visit Requests',
    module: 'Pastoral Care', category: 'Registers', builtIn: true,
    describes: 'Visit requests raised from the mobile app.',
    producedAt: 'Pastoral Care ▸ Requests ▸ Print'
  },
  {
    key: 'PASTORAL_VISIT_REGISTER', label: 'Pastoral Care Register — Visits Done',
    module: 'Pastoral Care', category: 'Registers', builtIn: true,
    describes: 'Home visits the church carried out.',
    producedAt: 'Pastoral Care ▸ Visits Done ▸ Print'
  },
  {
    key: 'COUNSELING_REGISTER', label: 'Pastoral Care Register — Counselling',
    module: 'Pastoral Care', category: 'Registers', builtIn: true,
    describes: 'Counselling sessions and their status.',
    producedAt: 'Pastoral Care ▸ Counseling ▸ Print'
  },
  {
    key: 'SICK_TRACKING_REGISTER', label: 'Pastoral Care Register — Sick Tracking',
    module: 'Pastoral Care', category: 'Registers', builtIn: true,
    describes: 'Sick members being followed up.',
    producedAt: 'Pastoral Care ▸ Sick Member Tracking ▸ Print'
  },
  {
    key: 'PASTORAL_CARE_RECORD', label: 'Pastoral Care Record',
    module: 'Pastoral Care', category: 'Records', builtIn: true,
    describes: 'One visit, request, counselling session or sick log with all its details.',
    producedAt: 'Pastoral Care ▸ any tab ▸ row ▸ View ▸ Print'
  },
  {
    key: 'YOUTH_FELLOWSHIP_RECORD', label: 'Youth Fellowship Record',
    module: 'Youth Fellowship', category: 'Records',
    describes: 'One fellowship: its age and gender rule, and everyone in the register who belongs to it.',
    producedAt: 'Fellowship ▸ Fellowships ▸ row menu or View ▸ Print'
  },
  {
    key: 'YOUTH_PROGRAM_RECORD', label: 'Youth Programme Record',
    module: 'Youth Fellowship', category: 'Records',
    describes: 'One programme or event: date, fellowship, attendance, budget and notes.',
    producedAt: 'Fellowship ▸ Programs & Events ▸ row menu or View ▸ Print'
  },
  {
    key: 'YOUTH_REGISTER', label: 'Youth Register — Fellowships',
    module: 'Youth Fellowship', category: 'Registers', builtIn: true, listColumns: 10,
    describes: 'Fellowships with their age band, leader and meeting day.',
    producedAt: 'Fellowship ▸ Fellowships ▸ Print'
  },
  {
    key: 'YOUTH_PROGRAM_REGISTER', label: 'Youth Register — Programmes',
    module: 'Youth Fellowship', category: 'Registers', builtIn: true, listColumns: 10,
    describes: 'Programmes and events with attendance and budget.',
    producedAt: 'Fellowship ▸ Programs & Events ▸ Print'
  },
  {
    key: 'CALENDAR_AGENDA', label: 'Calendar Agenda',
    module: 'Church Calendar', category: 'Registers',
    describes: 'The month\u2019s events as a date-ordered schedule sheet.',
    producedAt: 'Church Calendar ▸ Print'
  },
  {
    key: 'CELEBRATION_SHEET', label: 'Birthday & Anniversary Sheet',
    module: 'Birthday & Anniversary', category: 'Registers',
    describes: 'Upcoming birthdays and wedding anniversaries, for announcements.',
    producedAt: 'Birthday & Anniversary ▸ Print'
  },
  {
    key: 'BILL_REGISTER', label: 'Bill Register',
    module: 'Statutory Payments', category: 'Registers',
    describes: 'Recurring statutory dues and when they fall.',
    producedAt: 'Statutory Payments ▸ Print'
  }
];

export const DOCUMENT_CATEGORIES: DocumentCategory[] =
  ['Certificates', 'Records', 'Receipts', 'Financial', 'Registers'];

export const documentFor = (key?: string): PrintDocumentDef | undefined =>
  PRINT_DOCUMENTS.find((d) => d.key === key);

/** The labels of the must-print fields a saved layout leaves out. */
export const missingFields = (key: string | undefined, html: string): string[] => {
  const doc = documentFor(key);
  if (!doc?.mustPrint?.length) return [];
  const labels = new Map(fieldsFor(key).flatMap((g) => g.fields).map((f) => [f.token, f.label]));
  return doc.mustPrint
    .filter((token) => !new RegExp('\\{\\{\\s*' + token + '\\s*\\}\\}').test(html))
    .map((token) => labels.get(token) || token);
};

/** The name to show for a stored template's document type. */
export const documentLabel = (key?: string): string =>
  documentFor(key)?.label || key || 'Unknown document';

// ---------------------------------------------------------------------------
// What each document can say, in the administrator's words
// ---------------------------------------------------------------------------

export interface FieldChoice {
  /** What the administrator reads, e.g. "Member Name". */
  label: string;
  /** What is written into the template, e.g. `name`. */
  token: string;
}

export interface FieldGroup {
  group: string;
  fields: FieldChoice[];
}

const CHURCH: FieldGroup = {
  group: 'Church',
  fields: [
    { label: 'Church Name', token: 'churchName' },
    { label: 'Church Address', token: 'churchAddress' },
    { label: 'Contact Number', token: 'churchContactNo' },
    { label: 'Church Logo', token: 'churchLogo' },
    { label: "Today's Date", token: 'currentDate' },
    { label: 'Current Time', token: 'currentTime' },
    { label: 'Printed By', token: 'generatedBy' }
  ]
};

const MEMBER: FieldGroup = {
  group: 'Member',
  fields: [
    { label: 'Member Name', token: 'name' },
    { label: 'Member ID', token: 'memberId' },
    { label: "Father / Guardian", token: 'relativeName' },
    { label: 'Date of Birth', token: 'dateOfBirth' },
    { label: 'Gender', token: 'gender' },
    { label: 'Marital Status', token: 'maritalStatus' },
    { label: 'Date of Marriage', token: 'dateOfMarriage' },
    { label: 'Blood Group', token: 'bloodGroup' },
    { label: 'Education', token: 'education' },
    { label: 'Occupation', token: 'occupation' },
    { label: 'Mobile Number', token: 'mobileNumber' },
    { label: 'Address', token: 'location' },
    { label: 'Family ID', token: 'familyId' },
    { label: 'Head of Family', token: 'headOfFamily' },
    { label: 'Relation to Head', token: 'relationToHead' },
    { label: 'Member Status', token: 'memberStatus' },
    { label: 'Membership Date', token: 'membershipDate' },
    { label: 'Baptism Date', token: 'baptismDate' },
    { label: 'Confirmation Date', token: 'confirmationDate' },
    { label: 'Photograph', token: 'photoDataUrl' }
  ]
};

const FAMILY: FieldGroup = {
  group: 'Family',
  fields: [
    { label: 'Family ID', token: 'familyId' },
    { label: 'Family Name', token: 'familyName' },
    { label: 'Membership Number', token: 'membershipNo' },
    { label: 'Head of Family', token: 'headOfFamily' },
    { label: "Head's Gender", token: 'headGender' },
    { label: 'Parish', token: 'parish' },
    { label: 'Contact Number', token: 'contactNumber' },
    { label: 'Email', token: 'email' },
    { label: 'Address', token: 'address' },
    { label: 'Status', token: 'status' }
  ]
};

const BAPTISM: FieldGroup = {
  group: 'Baptism',
  fields: [
    { label: 'Register Number', token: 'registerNo' },
    { label: 'Certificate Number', token: 'certificateNo' },
    { label: 'Name of the Baptised', token: 'childName' },
    { label: 'Date of Birth', token: 'dob' },
    { label: 'Date of Baptism', token: 'baptismDate' },
    { label: "Parents' Names", token: 'parentsNames' },
    { label: 'Godparents', token: 'godparents' },
    { label: 'Officiating Minister', token: 'minister' },
    { label: 'Church', token: 'church' },
    { label: 'Remarks', token: 'remarks' }
  ]
};

const MARRIAGE: FieldGroup = {
  group: 'Marriage',
  fields: [
    { label: 'Register Number', token: 'registerNo' },
    { label: 'Certificate Number', token: 'certificateNo' },
    { label: 'Groom', token: 'groomName' },
    { label: 'Bride', token: 'brideName' },
    { label: "Parent's Name", token: 'parentName' },
    { label: 'Date of Marriage', token: 'marriageDate' },
    { label: 'Place of Marriage', token: 'marriagePlace' },
    { label: 'Officiating Minister', token: 'minister' },
    { label: 'First Witness', token: 'witness1' },
    { label: 'Second Witness', token: 'witness2' },
    { label: 'Registration Details', token: 'registrationDetails' }
  ]
};

const COMMUNION: FieldGroup = {
  group: 'First Communion',
  fields: [
    { label: 'Register Number', token: 'communionRegisterNo' },
    { label: 'Communion Number', token: 'communionNo' },
    { label: 'Certificate Number', token: 'certificateNo' },
    { label: 'Name', token: 'personName' },
    { label: 'Baptism Reference', token: 'baptismReference' },
    { label: 'Date of Communion', token: 'communionDate' },
    { label: 'Officiating Minister', token: 'minister' },
    { label: 'Church', token: 'church' },
    { label: 'Remarks', token: 'remarks' }
  ]
};

const CONFIRMATION: FieldGroup = {
  group: 'Confirmation',
  fields: [
    { label: 'Register Number', token: 'confirmationRegisterNo' },
    { label: 'Confirmation Number', token: 'confirmationNo' },
    { label: 'Certificate Number', token: 'certificateNo' },
    { label: 'Name', token: 'personName' },
    { label: 'Baptism Reference', token: 'baptismReference' },
    { label: 'Date of Confirmation', token: 'confirmationDate' },
    { label: 'Bishop', token: 'bishopName' },
    { label: 'Church', token: 'church' },
    { label: 'Remarks', token: 'remarks' }
  ]
};

const BANNS: FieldGroup = {
  group: 'Banns of Marriage',
  fields: [
    { label: 'Bann Number', token: 'bannNo' },
    { label: 'Groom', token: 'groomName' },
    { label: "Groom's Church", token: 'groomChurch' },
    { label: 'Bride', token: 'brideName' },
    { label: "Bride's Church", token: 'brideChurch' },
    { label: 'First Publication', token: 'firstDate' },
    { label: 'Second Publication', token: 'secondDate' },
    { label: 'Third Publication', token: 'thirdDate' },
    { label: 'Officiating Presbyter', token: 'officiatingPresbyter' },
    { label: 'Objections', token: 'objections' },
    { label: 'Approval Status', token: 'approvalStatus' }
  ]
};

const RECEIPT: FieldGroup = {
  group: 'Receipt',
  fields: [
    { label: 'Receipt Number', token: 'receipt' },
    { label: 'Date', token: 'date' },
    { label: 'Amount', token: 'amount' },
    { label: 'Received From', token: 'donorName' },
    { label: 'Purpose', token: 'purpose' },
    { label: 'Source', token: 'source' },
    { label: 'Given By', token: 'givenBy' },
    { label: 'Counted By', token: 'countedBy' },
    { label: 'Verified By', token: 'verifiedBy' }
  ]
};

const BURIAL: FieldGroup = {
  group: 'Burial',
  fields: [
    { label: 'Name of the Deceased', token: 'deceasedName' },
    { label: 'Date of Death', token: 'dateOfDeath' },
    { label: 'Date of Burial', token: 'date' },
    { label: 'Plot', token: 'assignedPlot' },
    { label: 'Next of Kin', token: 'nextOfKin' },
    { label: 'Member ID', token: 'memberId' },
    { label: 'Notes', token: 'notes' }
  ]
};

const MISSIONARY: FieldGroup = {
  group: 'Missionary',
  fields: [
    { label: 'Full Name', token: 'fullName' },
    { label: 'Record ID', token: 'recordId' },
    { label: 'Date of Birth', token: 'dob' },
    { label: 'Gender', token: 'gender' },
    { label: 'Mobile Number', token: 'mobileNumber' },
    { label: 'Email', token: 'email' },
    { label: 'Mission Field', token: 'missionField' },
    { label: 'Organisation', token: 'organization' },
    { label: 'Ministry Type', token: 'ministryType' },
    { label: 'Location', token: 'location' },
    { label: 'Date Joined', token: 'dateJoined' }
  ]
};

const REPORT_CARD: FieldGroup = {
  group: 'Report Card',
  fields: [
    { label: 'Student Name', token: 'studentName' },
    { label: 'Class / Section', token: 'className' },
    { label: 'Academic Year', token: 'academicYear' }
  ]
};

const EXAM_ROW: FieldGroup = {
  group: 'Each exam row',
  fields: [
    { label: 'Row Number', token: 'index' },
    { label: 'Exam', token: 'exam' },
    { label: 'Subject', token: 'subject' },
    { label: 'Date', token: 'date' },
    { label: 'Total Marks', token: 'total' },
    { label: 'Marks Scored', token: 'scored' },
    { label: 'Result', token: 'result' }
  ]
};

const LIST: FieldGroup = {
  group: 'The list',
  fields: [
    { label: 'List Title', token: 'title' },
    { label: 'What it covers', token: 'reference' },
    { label: 'Number of Rows', token: 'rowCount' }
  ]
};

// Named "Each row" so a starting layout is built as a table: under its old
// name ("Each statement row") the layout came out as a one-record sheet and
// printed no rows at all.
const STATEMENT_ROW: FieldGroup = {
  group: 'Each row',
  fields: [
    { label: 'Row Number', token: 'index' },
    { label: 'Date', token: 'date' },
    { label: 'Particulars', token: 'particulars' },
    { label: 'Amount', token: 'amount' },
    { label: 'Receipt', token: 'receipt' },
    { label: 'Payment', token: 'payment' },
    { label: 'Balance', token: 'balance' }
  ]
};

/**
 * A register prints the module's own records untouched, so its row fields are
 * that register's own columns. They are listed per register rather than
 * generically: offering a field a register does not store would print a blank
 * and leave an administrator no way to see why.
 */
const rowGroup = (group: string, fields: FieldChoice[]): FieldGroup => ({ group, fields });

const ROW_NUMBER: FieldChoice = { label: 'Row Number', token: 'index' };
const RECORD_ID: FieldChoice = { label: 'Record ID', token: 'recordId' };

const ATTENDANCE_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Service Date', token: 'date' },
  { label: 'Service Time', token: 'serviceTime' },
  { label: 'Event / Service', token: 'eventName' },
  { label: 'Venue', token: 'venue' },
  { label: 'Total Attendance', token: 'attendees' },
  { label: 'Men', token: 'menCount' },
  { label: 'Women', token: 'womenCount' },
  { label: 'Youth', token: 'youthCount' },
  { label: 'Children', token: 'childrenCount' },
  { label: 'New Visitors', token: 'newVisitors' },
  { label: 'Notes', token: 'notes' }
]);

/** The statement period, printed above every Accounts report. */
const PERIOD: FieldGroup = {
  group: 'Period',
  fields: [{ label: 'Period', token: 'reference' }]
};

const LEDGER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Voucher ID', token: 'voucherId' },
  { label: 'Date', token: 'date' },
  { label: 'Account Head', token: 'head' },
  { label: 'Particulars', token: 'particulars' },
  { label: 'Mode', token: 'mode' },
  { label: 'Type', token: 'type' },
  { label: 'Amount', token: 'amount' }
]);

const LEDGER_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'Total Income', token: 'totalIncome' },
    { label: 'Total Expenditure', token: 'totalExpense' },
    { label: 'Net (Income − Expenditure)', token: 'net' }
  ]
};

const BOOK_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Date', token: 'date' },
  { label: 'Voucher', token: 'voucherId' },
  { label: 'Particulars', token: 'particulars' },
  { label: 'Account Head', token: 'head' },
  { label: 'Receipt', token: 'receipt' },
  { label: 'Payment', token: 'payment' },
  { label: 'Balance', token: 'balance' }
]);

const BOOK_OPENING: FieldGroup = {
  group: 'Opening',
  fields: [{ label: 'Opening Balance', token: 'openingBalance' }]
};

const BOOK_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'Total Receipts', token: 'totalReceipts' },
    { label: 'Total Payments', token: 'totalPayments' },
    { label: 'Closing Balance', token: 'closingBalance' }
  ]
};

const TRIAL_ROW = rowGroup('Each row', [
  { label: 'Account Head / Particulars', token: 'particulars' },
  { label: 'Debit (Dr.)', token: 'debit' },
  { label: 'Credit (Cr.)', token: 'credit' }
]);

const TRIAL_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'Total Debit', token: 'totalDebit' },
    { label: 'Total Credit', token: 'totalCredit' },
    { label: 'Balanced', token: 'balanced' }
  ]
};

const HEAD_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Section', token: 'section' },
  { label: 'Account Head', token: 'particulars' },
  { label: 'Amount', token: 'amount' }
]);

const PNL_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'Total Income', token: 'totalIncome' },
    { label: 'Total Expenditure', token: 'totalExpense' },
    { label: 'Surplus / Deficit', token: 'surplusDeficit' }
  ]
};

const SUMMARY_FIGURES: FieldGroup = {
  group: 'Summary',
  fields: [
    { label: 'Total Income', token: 'totalIncome' },
    { label: 'Total Expenditure', token: 'totalExpense' },
    { label: 'Surplus / Deficit', token: 'surplusDeficit' },
    { label: 'Opening Cash', token: 'openingCash' },
    { label: 'Opening Bank', token: 'openingBank' },
    { label: 'Closing Cash', token: 'closingCash' },
    { label: 'Closing Bank', token: 'closingBank' },
    { label: 'Closing Fund', token: 'closingFund' }
  ]
};

const BALANCE_ROW = rowGroup('Each row', [
  { label: 'Side', token: 'section' },
  { label: 'Particulars', token: 'particulars' },
  { label: 'Amount', token: 'amount' }
]);

const BALANCE_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'As On', token: 'asOn' },
    { label: 'Total Liabilities & Funds', token: 'totalLiabilities' },
    { label: 'Total Assets', token: 'totalAssets' }
  ]
};

const AUDIT_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Original ID', token: 'origId' },
  { label: 'Source', token: 'auditType' },
  { label: 'Txn Date', token: 'date' },
  { label: 'Amount', token: 'amount' },
  { label: 'Reason', token: 'deleteReason' },
  { label: 'Requested By', token: 'deleteRequestedBy' },
  { label: 'Deleted On', token: 'deletedAt' },
  { label: 'Approved By', token: 'deleteApprovedBy' }
]);

const AUDIT_ALERTS: FieldGroup = {
  group: 'Audit alerts',
  fields: [
    { label: 'High Value Transactions', token: 'highValueCount' },
    { label: 'Voided / Deleted Records', token: 'voidedCount' }
  ]
};

const BUDGET_LINE_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Head / Line', token: 'particulars' },
  { label: 'Annual Budget', token: 'budget' },
  { label: 'Expected to Date', token: 'expected' },
  { label: 'Actual', token: 'actual' },
  { label: 'Variance', token: 'variance' },
  { label: 'Used %', token: 'usedPct' }
]);

const BUDGET_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'Total Budget', token: 'totalBudget' },
    { label: 'Total Actual', token: 'totalActual' },
    { label: 'Total Variance', token: 'totalVariance' }
  ]
};

const FY_MONTH_LABELS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const BUDGET_MONTH_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Head', token: 'particulars' },
  ...FY_MONTH_LABELS.map((m, i) => ({ label: m, token: `m${i + 1}` })),
  { label: 'Total', token: 'total' },
  { label: 'Allotted', token: 'allotted' }
]);

const BUDGET_MONTH_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'Total Spent', token: 'totalSpent' },
    { label: 'Total Allotted', token: 'totalAllotted' }
  ]
};

const EXPENSE_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Expense ID', token: 'internalId' },
  { label: 'Date', token: 'date' },
  { label: 'Category', token: 'category' },
  { label: 'Expense', token: 'expenseName' },
  { label: 'Paid To', token: 'paidTo' },
  { label: 'Mode', token: 'paymentMode' },
  { label: 'Reference', token: 'referenceNo' },
  { label: 'Remarks', token: 'remarks' },
  { label: 'Amount', token: 'amount' },
  { label: 'Receipt No', token: 'receiptNo' },
  { label: 'Created By', token: 'createdBy' }
]);

const EXPENSE_TOTALS: FieldGroup = {
  group: 'Totals',
  fields: [
    { label: 'Records', token: 'rowCount' },
    { label: 'Total', token: 'total' }
  ]
};

const MEMBER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Member ID', token: 'memberId' },
  { label: 'Member Name', token: 'name' },
  { label: 'Gender', token: 'gender' },
  { label: 'Date of Birth', token: 'dateOfBirth' },
  { label: 'Mobile Number', token: 'mobileNumber' },
  { label: 'Family ID', token: 'familyId' },
  // The row stores only the family id, so the directory carries the head's name
  // alongside it — "FAM-118" on its own says nothing about whose house it is.
  { label: 'Head of Family', token: 'headOfFamily' },
  { label: 'Relation to Head', token: 'relationToHead' },
  { label: 'Member Status', token: 'memberStatus' }
]);

const FAMILY_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Family ID', token: 'familyId' },
  { label: 'Head of Family', token: 'headOfFamily' },
  { label: 'Family Name', token: 'familyName' },
  { label: 'Membership Number', token: 'membershipNo' },
  { label: 'Contact Number', token: 'contactNumber' },
  { label: 'Address', token: 'address' },
  { label: 'Status', token: 'status' }
]);

const EVENT_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Event ID', token: 'eventId' },
  { label: 'Event Name', token: 'eventName' },
  { label: 'Category', token: 'category' },
  { label: 'Date', token: 'eventDate' },
  { label: 'Time', token: 'time' },
  { label: 'Venue', token: 'venue' },
  { label: 'Organiser', token: 'organiser' },
  { label: 'Budget', token: 'budget' },
  { label: 'Status', token: 'eventStatus' }
]);

const BILL_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Category', token: 'particulars' },
  { label: 'Account Number', token: 'account' },
  { label: 'Provider', token: 'provider' },
  { label: 'Department', token: 'department' },
  { label: 'Billing Cycle', token: 'frequency' },
  { label: 'Due Date', token: 'date' },
  { label: 'Responsible Person', token: 'responsible' },
  { label: 'Amount', token: 'amount' }
]);

/**
 * Choir, Meetings, Cemetery and Assets print whichever tab the administrator
 * is looking at, and each tab stores a different set of columns. A template is
 * therefore written for one tab, so the fields are offered per tab rather than
 * merged into a single list that would print blanks on the other tabs.
 */
const CHOIR_MEMBER_ROW = rowGroup('Each row - Members tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Name', token: 'name' },
  { label: 'Voice Type', token: 'voiceType' },
  { label: 'Role', token: 'role' },
  { label: 'Member Status', token: 'memberStatus' },
  { label: 'Phone', token: 'phone' },
  { label: 'Joined Date', token: 'joinedDate' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const CHOIR_SCHEDULE_ROW = rowGroup('Each row - Practice & Events tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Event Type', token: 'eventType' },
  { label: 'Date', token: 'date' },
  { label: 'Time', token: 'time' },
  { label: 'Conductor', token: 'conductor' },
  { label: 'Headcount', token: 'headcount' },
  { label: 'Songs', token: 'songs' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const CHOIR_ASSET_ROW = rowGroup('Each row - Choir Assets tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Title', token: 'title' },
  { label: 'Category', token: 'category' },
  { label: 'Asset Status', token: 'assetStatus' },
  { label: 'Assigned To', token: 'assignedTo' },
  { label: 'Date Added', token: 'dateAdded' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const MEETING_ROW = rowGroup('Each row - Meetings tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Title', token: 'title' },
  { label: 'Type', token: 'type' },
  { label: 'Committee', token: 'committee' },
  { label: 'Date', token: 'date' },
  { label: 'Time', token: 'time' },
  { label: 'Venue', token: 'venue' },
  { label: 'Meeting Status', token: 'meetingStatus' },
  { label: 'Remarks', token: 'remarks' },
  { label: 'Created By', token: 'createdBy' }
]);

const MINUTE_ROW = rowGroup('Each row - Minutes tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Meeting ID', token: 'mtgId' },
  { label: 'Agenda Topic', token: 'agendaTopic' },
  { label: 'Discussion', token: 'discussion' },
  { label: 'Decision', token: 'decision' },
  { label: 'Resolution Number', token: 'resolutionNo' },
  { label: 'Budget', token: 'budget' },
  { label: 'Minute Status', token: 'minuteStatus' },
  { label: 'Date Recorded', token: 'dateRecorded' },
  { label: 'Created By', token: 'createdBy' }
]);

const ACTION_ROW = rowGroup('Each row - Action Items tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Minute ID', token: 'minId' },
  { label: 'Task', token: 'task' },
  { label: 'Assigned To', token: 'assignedTo' },
  { label: 'Due Date', token: 'dueDate' },
  { label: 'Action Status', token: 'actionStatus' },
  { label: 'Remarks', token: 'remarks' },
  { label: 'Created By', token: 'createdBy' }
]);

const PLOT_ROW = rowGroup('Each row - Plots & Allotment tabs', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Plot Number', token: 'plotNumber' },
  { label: 'Section', token: 'section' },
  { label: 'Availability', token: 'status' },
  { label: 'Allotted To', token: 'allottedTo' },
  { label: 'Date Added', token: 'dateAdded' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const BURIAL_ROW = rowGroup('Each row - Burials tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Burial Date', token: 'date' },
  { label: 'Deceased Name', token: 'deceasedName' },
  { label: 'Member ID', token: 'memberId' },
  { label: 'Date of Death', token: 'dateOfDeath' },
  { label: 'Plot Number', token: 'plotNumber' },
  { label: 'Section', token: 'section' },
  { label: 'Full Plot', token: 'fullPlot' },
  { label: 'Next of Kin', token: 'nextOfKin' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const CEMETERY_MAINTENANCE_ROW = rowGroup('Each row - Maintenance tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Date', token: 'date' },
  { label: 'Area', token: 'area' },
  { label: 'Task Type', token: 'taskType' },
  { label: 'Cost', token: 'cost' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

// The first seven fields of each list are what a starting layout prints, so
// they are the ones that identify the line.
const ASSET_REGISTRY_ROW = rowGroup('Each row - Registry & Write-Off tabs', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Asset Name', token: 'name' },
  { label: 'Category', token: 'category' },
  { label: 'Quantity', token: 'quantity' },
  { label: 'Total Cost / Value', token: 'totalCost' },
  { label: 'Location', token: 'location' },
  { label: 'Current Value', token: 'currentValue' },
  { label: 'Unit Cost', token: 'purchaseCost' },
  { label: 'Brand', token: 'brand' },
  { label: 'Serial Number', token: 'serialNo' },
  { label: 'Purchase Date', token: 'purchaseDate' },
  { label: 'Acquired By', token: 'acquisition' },
  { label: 'Vendor / Sponsor', token: 'source' },
  { label: 'Department', token: 'department' },
  { label: 'Warranty Expiry', token: 'warrantyExpiry' },
  { label: 'Deleted / Written Off On', token: 'deletedAt' },
  { label: 'Reason', token: 'deleteReason' }
]);

const ASSET_ALLOCATION_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Date', token: 'date' },
  { label: 'Asset', token: 'assetName' },
  { label: 'Custodian', token: 'custodian' },
  { label: 'New Location', token: 'newLocation' },
  { label: 'Purpose / Notes', token: 'purpose' },
  { label: 'New Department', token: 'newDepartment' },
  { label: 'Asset ID', token: 'assetId' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const ASSET_MAINTENANCE_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Service Date', token: 'date' },
  { label: 'Asset', token: 'assetName' },
  { label: 'Vendor / Technician', token: 'vendor' },
  { label: 'Problem & Solution', token: 'problem' },
  { label: 'Cost', token: 'cost' },
  { label: 'Next Service Due', token: 'nextServiceDate' },
  { label: 'Asset ID', token: 'assetId' },
  { label: 'Created By', token: 'createdBy' }
]);

const ASSET_TRANSFER: FieldGroup = {
  group: 'Transfer',
  fields: [
    { label: 'Record ID', token: 'recordId' },
    { label: 'Transfer Date', token: 'date' },
    { label: 'Asset', token: 'assetName' },
    { label: 'Asset ID', token: 'assetId' },
    { label: 'Custodian / Receiver', token: 'custodian' },
    { label: 'New Location', token: 'newLocation' },
    { label: 'New Department', token: 'newDepartment' },
    { label: 'Purpose / Notes', token: 'purpose' },
    { label: 'Recorded By', token: 'createdBy' }
  ]
};

const ASSET_MAINTENANCE: FieldGroup = {
  group: 'Maintenance',
  fields: [
    { label: 'Record ID', token: 'recordId' },
    { label: 'Service Date', token: 'date' },
    { label: 'Asset', token: 'assetName' },
    { label: 'Asset ID', token: 'assetId' },
    { label: 'Vendor / Technician', token: 'vendor' },
    { label: 'Repair Cost', token: 'cost' },
    { label: 'Next Service Due', token: 'nextServiceDate' },
    { label: 'Problem & Solution', token: 'problem' },
    { label: 'Recorded By', token: 'createdBy' }
  ]
};

const TERM_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Term Name', token: 'name' },
  { label: 'Start Date', token: 'startDate' },
  { label: 'End Date', token: 'endDate' },
  { label: 'Term Status', token: 'termStatus' },
  { label: 'Remarks', token: 'remarks' },
  { label: 'Created By', token: 'createdBy' },
  { label: 'Members Elected', token: 'memberCount' }
]);

// A member's service period also fills startDate / endDate, so a layout
// written with the term's date fields still prints the member's dates.
const COMMITTEE_MEMBER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Name', token: 'name' },
  { label: 'Role', token: 'role' },
  { label: 'Term', token: 'termName' },
  { label: 'From Date', token: 'fromDate' },
  { label: 'To Date', token: 'toDate' },
  { label: 'Member Status', token: 'memberStatus' },
  { label: 'Remarks / Portfolio Notes', token: 'remarks' },
  { label: 'Created By', token: 'createdBy' },
  { label: 'Term ID', token: 'termId' },
  { label: 'Period', token: 'period' }
]);

const SECRETARY_HANDOVER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Date', token: 'date' },
  { label: 'Outgoing Secretary', token: 'outgoing' },
  { label: 'Incoming Secretary', token: 'incoming' },
  { label: 'Witness', token: 'witness' },
  { label: 'Handover Status', token: 'handoverStatus' },
  { label: 'Items Handed Over', token: 'checklist' },
  { label: 'Created By', token: 'createdBy' }
]);

const COMMITTEE_MEMBER: FieldGroup = {
  group: 'Committee member',
  fields: [
    { label: 'Record ID', token: 'recordId' },
    { label: 'Name', token: 'name' },
    { label: 'Role', token: 'role' },
    { label: 'Term', token: 'termName' },
    { label: 'Term ID', token: 'termId' },
    { label: 'From Date', token: 'fromDate' },
    { label: 'To Date', token: 'toDate' },
    { label: 'Member Status', token: 'memberStatus' },
    { label: 'Remarks / Portfolio Notes', token: 'remarks' },
    { label: 'Created By', token: 'createdBy' }
  ]
};

const COMMITTEE_TERM: FieldGroup = {
  group: 'Electoral term',
  fields: [
    { label: 'Record ID', token: 'recordId' },
    { label: 'Term Name', token: 'name' },
    { label: 'Start Date', token: 'startDate' },
    { label: 'End Date', token: 'endDate' },
    { label: 'Term Status', token: 'termStatus' },
    { label: 'Members Elected', token: 'memberCount' },
    { label: 'Remarks', token: 'remarks' },
    { label: 'Created By', token: 'createdBy' }
  ]
};

const SECRETARY_HANDOVER: FieldGroup = {
  group: 'Secretary handover',
  fields: [
    { label: 'Record ID', token: 'recordId' },
    { label: 'Handover Date', token: 'date' },
    { label: 'Outgoing Secretary', token: 'outgoing' },
    { label: 'Incoming Secretary', token: 'incoming' },
    { label: 'Witness', token: 'witness' },
    { label: 'Handover Status', token: 'handoverStatus' },
    { label: 'Office Keys & Safe Access', token: 'adminKeysLabel' },
    { label: 'Committee Minute Books', token: 'minuteBooksLabel' },
    { label: 'Financial Records & Cheque Books', token: 'finRecordsLabel' },
    { label: 'Property & Legal Documents', token: 'legalDocsLabel' },
    { label: 'Created By', token: 'createdBy' }
  ]
};

const TENURE_ROW = rowGroup('Each row - Tenure tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Pastor', token: 'name' },
  { label: 'Designation', token: 'designation' },
  { label: 'Appointment Order', token: 'apptOrder' },
  { label: 'Start Date', token: 'startDate' },
  { label: 'End Date', token: 'endDate' },
  { label: 'Previous Church', token: 'prevChurch' },
  { label: 'Next Church', token: 'nextChurch' },
  { label: 'Tenure Status', token: 'tenureStatus' },
  { label: 'Remarks', token: 'remarks' },
  { label: 'Created By', token: 'createdBy' }
]);

const POSTING_ROW = rowGroup('Each row - Posting tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Pastor', token: 'name' },
  { label: 'Designation', token: 'designation' },
  { label: 'Diocese', token: 'diocese' },
  { label: 'Order Date', token: 'orderDate' },
  { label: 'Effective Date', token: 'effectiveDate' },
  { label: 'Reason', token: 'reason' },
  { label: 'Posting Status', token: 'postingStatus' },
  { label: 'Created By', token: 'createdBy' }
]);

const PASTOR_HANDOVER_ROW = rowGroup('Each row - Handover tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Date', token: 'date' },
  { label: 'Outgoing Pastor', token: 'outgoing' },
  { label: 'Incoming Pastor', token: 'incoming' },
  { label: 'Witness', token: 'witness' },
  { label: 'Approved By', token: 'approvedBy' },
  { label: 'Handover Status', token: 'handoverStatus' },
  { label: 'Created By', token: 'createdBy' }
]);

// The first seven fields of each list are what a starting layout prints, so
// Date, Member, Pastor and Location lead every tab.
const VISIT_REQUEST_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Preferred Date', token: 'preferredDate' },
  { label: 'Requested By', token: 'requestedBy' },
  { label: 'Assigned Pastor', token: 'assignedPastor' },
  { label: 'Location', token: 'location' },
  { label: 'Request Type', token: 'requestType' },
  { label: 'Contact Number', token: 'contactNumber' },
  { label: 'Notes from the Member', token: 'userNotes' },
  { label: "Pastor's Notes", token: 'pastorNotes' },
  { label: 'Status', token: 'statusLabel' },
  { label: 'Created By', token: 'createdBy' }
]);

const PASTORAL_VISIT_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Date', token: 'date' },
  { label: 'Member', token: 'memberName' },
  { label: 'Pastor', token: 'pastorName' },
  { label: 'Location', token: 'location' },
  { label: 'Purpose', token: 'purpose' },
  { label: 'Notes', token: 'notes' },
  { label: 'Status', token: 'statusLabel' },
  { label: 'Created By', token: 'createdBy' }
]);

const COUNSELING_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Date', token: 'date' },
  { label: 'Member', token: 'memberName' },
  { label: 'Pastor', token: 'pastorName' },
  { label: 'Location', token: 'location' },
  { label: 'Type', token: 'type' },
  { label: 'Session Status', token: 'sessionStatus' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const SICK_LOG_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Date', token: 'date' },
  { label: 'Member', token: 'memberName' },
  { label: 'Pastor', token: 'pastorName' },
  { label: 'Location', token: 'location' },
  { label: 'Follow-up Date', token: 'followUpDate' },
  { label: 'Notes', token: 'notes' },
  { label: 'Status', token: 'statusLabel' },
  { label: 'Created By', token: 'createdBy' }
]);

/** One pastoral care line of any tab, on its own sheet. */
const PASTORAL_RECORD: FieldGroup = {
  group: 'Pastoral care record',
  fields: [
    { label: 'Record ID', token: 'recordId' },
    { label: 'Record Type', token: 'recordType' },
    { label: 'Date', token: 'date' },
    { label: 'Member', token: 'memberName' },
    { label: 'Pastor', token: 'pastorName' },
    { label: 'Location / Place', token: 'location' },
    { label: 'Purpose / Type', token: 'purpose' },
    { label: 'Session Status', token: 'sessionStatus' },
    { label: 'Follow-up Date', token: 'followUpDate' },
    { label: 'Contact Number', token: 'contactNumber' },
    { label: 'Notes', token: 'notes' },
    { label: 'Status', token: 'statusLabel' },
    { label: 'Created By', token: 'createdBy' }
  ]
};

const YOUTH_GROUP_ROW = rowGroup('Each row - Fellowships tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Fellowship Name', token: 'name' },
  { label: 'Age From', token: 'ageFrom' },
  { label: 'Age To', token: 'ageTo' },
  { label: 'Gender', token: 'gender' },
  { label: 'Leader', token: 'leader' },
  { label: 'Meeting Day', token: 'meetingDay' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

const YOUTH_PROGRAM_ROW = rowGroup('Each row - Programmes tab', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Fellowship', token: 'fellowshipName' },
  { label: 'Date', token: 'date' },
  { label: 'Programme', token: 'title' },
  { label: 'Type', token: 'progType' },
  { label: 'Attendance', token: 'attendance' },
  { label: 'Budget', token: 'budget' },
  { label: 'Notes', token: 'notes' },
  { label: 'Created By', token: 'createdBy' }
]);

/** One fellowship, printed on its own. */
const YOUTH_FELLOWSHIP: FieldGroup = {
  group: 'Fellowship',
  fields: [
    RECORD_ID,
    { label: 'Fellowship Name', token: 'name' },
    { label: 'Age Group', token: 'ageBand' },
    { label: 'Age From', token: 'ageFrom' },
    { label: 'Age To', token: 'ageTo' },
    { label: 'Gender', token: 'gender' },
    { label: 'Members', token: 'memberCount' },
    { label: 'Remark', token: 'notes' },
    { label: 'Created By', token: 'createdBy' }
  ]
};

const YOUTH_FELLOWSHIP_MEMBER_ROW = rowGroup('Each member of the fellowship', [
  ROW_NUMBER,
  { label: 'Member Name', token: 'name' },
  { label: 'Member ID', token: 'memberId' },
  { label: 'Age', token: 'age' },
  { label: 'Gender', token: 'gender' },
  { label: 'Date of Birth', token: 'dateOfBirth' },
  { label: 'Mobile Number', token: 'mobileNumber' },
  { label: 'Family ID', token: 'familyId' },
  { label: 'Member Status', token: 'memberStatus' }
]);

/** One programme or event, printed on its own. */
const YOUTH_PROGRAM: FieldGroup = {
  group: 'Programme',
  fields: [
    RECORD_ID,
    { label: 'Programme / Event Title', token: 'title' },
    { label: 'Date', token: 'date' },
    { label: 'Fellowship', token: 'fellowshipName' },
    { label: 'Category', token: 'progType' },
    { label: 'Attendance', token: 'attendance' },
    { label: 'Budget Used', token: 'budget' },
    { label: 'Notes', token: 'notes' },
    { label: 'Created By', token: 'createdBy' }
  ]
};

const AGENDA_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Date', token: 'eventDate' },
  { label: 'Time', token: 'time' },
  { label: 'Event', token: 'eventName' },
  { label: 'Category', token: 'category' },
  { label: 'Venue', token: 'venue' },
  { label: 'Organiser', token: 'organiser' },
  { label: 'Status', token: 'eventStatus' }
]);

const CELEBRATION_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Name', token: 'name' },
  { label: 'Occasion', token: 'occasion' },
  { label: 'Date', token: 'date' },
  { label: 'Years', token: 'years' },
  { label: 'Member ID', token: 'memberId' },
  { label: 'Family ID', token: 'familyId' }
]);

const FAMILY_MEMBER_ROW = rowGroup('Each member of the family', [
  ROW_NUMBER,
  { label: 'Member ID', token: 'memberId' },
  { label: 'Member Name', token: 'name' },
  { label: 'Head of Family', token: 'headOfFamily' },
  { label: 'Relation to Head', token: 'relationToHead' },
  { label: 'Gender', token: 'gender' },
  { label: 'Date of Birth', token: 'dateOfBirth' },
  { label: 'Mobile Number', token: 'mobileNumber' },
  { label: 'Member Status', token: 'memberStatus' }
]);

/** A public family registration, as the family submitted it. */
const REGISTRATION: FieldGroup = {
  group: 'Registration',
  fields: [
    { label: 'Reference No.', token: 'referenceNo' },
    { label: 'Registration Status', token: 'registrationStatus' },
    { label: 'Submitted On', token: 'submittedAt' },
    { label: 'Approved On', token: 'approvedAt' },
    { label: 'Reason Not Approved', token: 'rejectReason' },
    { label: 'Family ID', token: 'familyId' },
    { label: 'Membership Number', token: 'membershipNo' },
    { label: 'Head of Family', token: 'headOfFamily' },
    { label: "Head's Mobile", token: 'headMobile' },
    { label: "Head's Date of Birth", token: 'headDateOfBirth' },
    { label: 'Family Address', token: 'address' },
    { label: 'Parish', token: 'parish' },
    { label: 'Family Email', token: 'email' },
    { label: 'People in Family', token: 'memberCount' }
  ]
};

const REGISTRATION_MEMBER_ROW = rowGroup('Each member of the family', [
  ROW_NUMBER,
  { label: 'Member Name', token: 'name' },
  { label: 'Relation to Head', token: 'relationToHead' },
  { label: 'Gender', token: 'gender' },
  { label: 'Date of Birth', token: 'dateOfBirth' },
  { label: 'Marital Status', token: 'maritalStatus' },
  { label: 'Mobile Number', token: 'mobileNumber' },
  { label: 'Member Status', token: 'memberStatus' },
  { label: 'Father / Husband Name', token: 'relativeName' },
  { label: 'Date of Marriage', token: 'dateOfMarriage' },
  { label: 'Blood Group', token: 'bloodGroup' },
  { label: 'Education', token: 'education' },
  { label: 'Occupation', token: 'occupation' },
  { label: 'Aadhaar Number (masked)', token: 'aadhaarNumber' },
  { label: 'Area / Locality', token: 'location' },
  { label: 'Membership No.', token: 'barcodeMembershipNo' },
  { label: 'Life Status', token: 'lifeStatus' },
  { label: 'Date of Death', token: 'dateOfDeath' },
  { label: 'Transferred To', token: 'transferChurch' },
  { label: 'Guest Since', token: 'guestFrom' },
  { label: 'Guest Until', token: 'guestUntil' },
  { label: 'Baptism Date', token: 'baptismDate' },
  { label: 'Confirmation Date', token: 'confirmationDate' },
  { label: 'Communion Status', token: 'communionStatus' }
]);

const BAPTISM_REGISTER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Register Number', token: 'registerNo' },
  { label: 'Certificate Number', token: 'certificateNo' },
  { label: 'Name of the Baptised', token: 'childName' },
  { label: 'Date of Birth', token: 'dob' },
  { label: 'Date of Baptism', token: 'baptismDate' },
  { label: "Parents' Names", token: 'parentsNames' },
  { label: 'Godparents', token: 'godparents' },
  { label: 'Officiating Minister', token: 'minister' },
  { label: 'Church', token: 'church' },
  { label: 'Remarks', token: 'remarks' }
]);

const COMMUNION_REGISTER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Register Number', token: 'communionRegisterNo' },
  { label: 'Communion Number', token: 'communionNo' },
  { label: 'Certificate Number', token: 'certificateNo' },
  { label: 'Name', token: 'personName' },
  { label: 'Baptism Reference', token: 'baptismReference' },
  { label: 'Date of Communion', token: 'communionDate' },
  { label: 'Officiating Minister', token: 'minister' },
  { label: 'Church', token: 'church' },
  { label: 'Remarks', token: 'remarks' }
]);

const CONFIRMATION_REGISTER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Register Number', token: 'confirmationRegisterNo' },
  { label: 'Confirmation Number', token: 'confirmationNo' },
  { label: 'Certificate Number', token: 'certificateNo' },
  { label: 'Name', token: 'personName' },
  { label: 'Baptism Reference', token: 'baptismReference' },
  { label: 'Date of Confirmation', token: 'confirmationDate' },
  { label: 'Bishop', token: 'bishopName' },
  { label: 'Church', token: 'church' },
  { label: 'Remarks', token: 'remarks' }
]);

const MARRIAGE_REGISTER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Register Number', token: 'registerNo' },
  { label: 'Certificate Number', token: 'certificateNo' },
  { label: 'Groom', token: 'groomName' },
  { label: 'Bride', token: 'brideName' },
  { label: 'Date of Marriage', token: 'marriageDate' },
  { label: 'Place of Marriage', token: 'marriagePlace' },
  { label: 'Officiating Minister', token: 'minister' },
  { label: 'First Witness', token: 'witness1' },
  { label: 'Second Witness', token: 'witness2' },
  { label: 'Registration Details', token: 'registrationDetails' }
]);

const BANNS_REGISTER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  RECORD_ID,
  { label: 'Bann Number', token: 'bannNo' },
  { label: 'Groom', token: 'groomName' },
  { label: "Groom's Church", token: 'groomChurch' },
  { label: 'Bride', token: 'brideName' },
  { label: "Bride's Church", token: 'brideChurch' },
  { label: 'First Publication', token: 'firstDate' },
  { label: 'Second Publication', token: 'secondDate' },
  { label: 'Third Publication', token: 'thirdDate' },
  { label: 'Officiating Presbyter', token: 'officiatingPresbyter' },
  { label: 'Approval Status', token: 'approvalStatus' }
]);

const SACRAMENT_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Member ID', token: 'memberId' },
  { label: 'Member Name', token: 'name' },
  { label: 'Family ID', token: 'familyId' },
  { label: 'Date of Birth', token: 'dateOfBirth' },
  { label: 'Age', token: 'age' },
  { label: 'Baptism', token: 'baptism' },
  { label: 'First Communion', token: 'communion' },
  { label: 'Confirmation', token: 'confirmation' },
  { label: 'Outstanding', token: 'outstanding' }
]);

const ROSTER_ROW = rowGroup('Each row', [
  ROW_NUMBER,
  { label: 'Student ID', token: 'studentId' },
  { label: 'Student Name', token: 'name' },
  { label: 'Member ID', token: 'memberId' },
  { label: 'Age', token: 'age' },
  { label: 'Family', token: 'familyName' },
  { label: 'Parent / Guardian', token: 'parentName' },
  { label: 'Phone', token: 'phone' },
  { label: 'Attendance', token: 'attendance' }
]);

/** The class a roster belongs to, named above its table. */
const CLASS: FieldGroup = {
  group: 'The class',
  fields: [
    { label: 'Class', token: 'className' },
    { label: 'Grade', token: 'gradeName' },
    { label: 'Teacher', token: 'teacher' },
    { label: 'Students Enrolled', token: 'rowCount' }
  ]
};

export const fieldsFor = (key?: string): FieldGroup[] => {
  switch (key) {
    case 'BAPTISM_CERTIFICATE':
      return [CHURCH, BAPTISM];
    case 'COMMUNION_CERTIFICATE':
      return [CHURCH, COMMUNION];
    case 'CONFIRMATION_CERTIFICATE':
      return [CHURCH, CONFIRMATION];
    case 'MARRIAGE_CERTIFICATE':
      return [CHURCH, MARRIAGE];
    case 'BANNS_NOTICE':
      return [CHURCH, BANNS];
    case 'MEMBER_RECORD':
      return [CHURCH, MEMBER];
    case 'FAMILY_RECORD':
      return [CHURCH, FAMILY, FAMILY_MEMBER_ROW];
    case 'FAMILY_REGISTRATION':
      return [CHURCH, REGISTRATION, REGISTRATION_MEMBER_ROW];
    case 'MISSIONARY_PROFILE':
      return [CHURCH, MISSIONARY];
    case 'BURIAL_RECORD':
      return [CHURCH, BURIAL];
    case 'OFFERING_RECEIPT':
      return [CHURCH, RECEIPT];
    case 'REPORT_CARD':
      return [CHURCH, REPORT_CARD, EXAM_ROW];
    case 'ACCOUNTS_LEDGER':
      return [CHURCH, LIST, PERIOD, LEDGER_ROW, LEDGER_TOTALS];
    case 'FINANCIAL_SUMMARY':
      return [CHURCH, LIST, PERIOD, SUMMARY_FIGURES, HEAD_ROW];
    case 'CASH_BOOK':
    case 'BANK_BOOK':
      return [CHURCH, LIST, PERIOD, BOOK_OPENING, BOOK_ROW, BOOK_TOTALS];
    case 'TRIAL_BALANCE':
      return [CHURCH, LIST, PERIOD, TRIAL_ROW, TRIAL_TOTALS];
    case 'INCOME_EXPENDITURE':
      return [CHURCH, LIST, PERIOD, HEAD_ROW, PNL_TOTALS];
    case 'BALANCE_SHEET':
      return [CHURCH, LIST, BALANCE_ROW, BALANCE_TOTALS];
    case 'ACCOUNTS_AUDIT_LOG':
      return [CHURCH, LIST, PERIOD, AUDIT_ALERTS, AUDIT_ROW];
    case 'BUDGET_REPORT':
      return [CHURCH, LIST, BUDGET_LINE_ROW, BUDGET_TOTALS];
    case 'BUDGET_MONTHLY_REPORT':
      return [CHURCH, LIST, BUDGET_MONTH_ROW, BUDGET_MONTH_TOTALS];
    case 'EXPENSE_LEDGER':
      return [CHURCH, LIST, EXPENSE_ROW, EXPENSE_TOTALS];
    case 'FINANCIAL_STATEMENT':
      return [CHURCH, LIST, STATEMENT_ROW];
    case 'MEMBER_DIRECTORY':
      return [CHURCH, LIST, MEMBER_ROW];
    case 'FAMILY_REGISTER':
      return [CHURCH, LIST, FAMILY_ROW];
    case 'ATTENDANCE_REGISTER':
      return [CHURCH, LIST, ATTENDANCE_ROW];
    case 'CEMETERY_REGISTER':
      return [CHURCH, LIST, PLOT_ROW];
    case 'CEMETERY_BURIAL_REGISTER':
      return [CHURCH, LIST, BURIAL_ROW];
    case 'CEMETERY_MAINTENANCE_REGISTER':
      return [CHURCH, LIST, CEMETERY_MAINTENANCE_ROW];
    case 'CHOIR_REGISTER':
      return [CHURCH, LIST, CHOIR_MEMBER_ROW];
    case 'CHOIR_SCHEDULE_REGISTER':
      return [CHURCH, LIST, CHOIR_SCHEDULE_ROW];
    case 'CHOIR_ASSET_REGISTER':
      return [CHURCH, LIST, CHOIR_ASSET_ROW];
    case 'ASSET_REGISTER':
      return [CHURCH, LIST, ASSET_REGISTRY_ROW];
    case 'ASSET_ALLOCATION_REGISTER':
      return [CHURCH, LIST, ASSET_ALLOCATION_ROW];
    case 'ASSET_MAINTENANCE_REGISTER':
      return [CHURCH, LIST, ASSET_MAINTENANCE_ROW];
    case 'ASSET_TRANSFER_RECORD':
      return [CHURCH, ASSET_TRANSFER];
    case 'ASSET_MAINTENANCE_RECORD':
      return [CHURCH, ASSET_MAINTENANCE];
    case 'EVENT_SCHEDULE':
      return [CHURCH, LIST, EVENT_ROW];
    case 'MINUTES_EXTRACT':
      return [CHURCH, LIST, MEETING_ROW];
    case 'MINUTES_REGISTER':
      return [CHURCH, LIST, MINUTE_ROW];
    case 'ACTION_ITEMS_REGISTER':
      return [CHURCH, LIST, ACTION_ROW];
    case 'BILL_REGISTER':
      return [CHURCH, LIST, BILL_ROW];
    case 'BAPTISM_REGISTER':
      return [CHURCH, LIST, BAPTISM_REGISTER_ROW];
    case 'COMMUNION_REGISTER':
      return [CHURCH, LIST, COMMUNION_REGISTER_ROW];
    case 'CONFIRMATION_REGISTER':
      return [CHURCH, LIST, CONFIRMATION_REGISTER_ROW];
    case 'MARRIAGE_REGISTER':
      return [CHURCH, LIST, MARRIAGE_REGISTER_ROW];
    case 'BANNS_REGISTER':
      return [CHURCH, LIST, BANNS_REGISTER_ROW];
    case 'SACRAMENT_STATUS':
      return [CHURCH, LIST, SACRAMENT_ROW];
    case 'SUNDAY_SCHOOL_ROSTER':
      return [CHURCH, LIST, CLASS, ROSTER_ROW];
    case 'COMMITTEE_REGISTER':
      return [CHURCH, LIST, TERM_ROW];
    case 'COMMITTEE_MEMBER_REGISTER':
      return [CHURCH, LIST, COMMITTEE_MEMBER_ROW];
    case 'SECRETARY_HANDOVER_REGISTER':
      return [CHURCH, LIST, SECRETARY_HANDOVER_ROW];
    case 'COMMITTEE_MEMBER_RECORD':
      return [CHURCH, COMMITTEE_MEMBER];
    case 'COMMITTEE_TERM_RECORD':
      return [CHURCH, COMMITTEE_TERM];
    case 'SECRETARY_HANDOVER_RECORD':
      return [CHURCH, SECRETARY_HANDOVER];
    case 'PASTOR_REGISTER':
      return [CHURCH, LIST, TENURE_ROW];
    case 'PASTOR_POSTING_REGISTER':
      return [CHURCH, LIST, POSTING_ROW];
    case 'PASTOR_HANDOVER_REGISTER':
      return [CHURCH, LIST, PASTOR_HANDOVER_ROW];
    case 'PASTORAL_CARE_REGISTER':
      return [CHURCH, LIST, VISIT_REQUEST_ROW];
    case 'PASTORAL_VISIT_REGISTER':
      return [CHURCH, LIST, PASTORAL_VISIT_ROW];
    case 'COUNSELING_REGISTER':
      return [CHURCH, LIST, COUNSELING_ROW];
    case 'SICK_TRACKING_REGISTER':
      return [CHURCH, LIST, SICK_LOG_ROW];
    case 'PASTORAL_CARE_RECORD':
      return [CHURCH, PASTORAL_RECORD];
    case 'YOUTH_REGISTER':
      return [CHURCH, LIST, YOUTH_GROUP_ROW];
    case 'YOUTH_PROGRAM_REGISTER':
      return [CHURCH, LIST, YOUTH_PROGRAM_ROW];
    case 'YOUTH_FELLOWSHIP_RECORD':
      return [CHURCH, YOUTH_FELLOWSHIP, YOUTH_FELLOWSHIP_MEMBER_ROW];
    case 'YOUTH_PROGRAM_RECORD':
      return [CHURCH, YOUTH_PROGRAM];
    case 'CALENDAR_AGENDA':
      return [CHURCH, LIST, AGENDA_ROW];
    case 'CELEBRATION_SHEET':
      return [CHURCH, LIST, CELEBRATION_ROW];
    default:
      return [CHURCH, LIST];
  }
};

// ---------------------------------------------------------------------------
// Sample values, so a layout can be judged before a real record exists
// ---------------------------------------------------------------------------

const CHURCH_SAMPLE = {
  churchName: 'CSI Christ Church, Devicode',
  churchAddress: '14 Church Street, Devicode 629 001',
  churchContactNo: '04652 234567'
};

/**
 * The preview has to show the same fields the picker offers, or an
 * administrator who inserts "Service Time" sees an empty cell and cannot tell
 * whether the template is wrong or the register simply has no such column.
 * Sample values are therefore derived from that register's own field list.
 */
const SAMPLE_ROW_COUNT = 3;

const sampleValue = (token: string, label: string, row: number): string | number => {
  const t = token.toLowerCase();
  if (t === 'index') return row;
  if (t.endsWith('id') || t === 'recordid') return `${token.slice(0, 3).toUpperCase()}-100${row}`;
  if (t.includes('date') || t === 'dob') return ['06 Sep 2026', '13 Sep 2026', '20 Sep 2026'][row - 1];
  if (t.includes('time')) return ['08:00 AM', '10:30 AM', '06:00 PM'][row - 1];
  if (t.includes('amount') || t.includes('cost') || t.includes('budget')) return ['12,400.00', '3,150.00', '48,900.00'][row - 1];
  if (t.includes('count') || t === 'quantity' || t === 'attendees') return [42, 18, 7][row - 1];
  if (t.includes('status')) return ['Active', 'Active', 'Active'][row - 1];
  if (t.includes('phone') || t.includes('mobile') || t.includes('contact')) return '9876543210';
  return `${label} ${row}`;
};

/** The first "Each row" group is the one a starting layout is built from. */
const rowFieldsFor = (key?: string): FieldChoice[] =>
  (fieldsFor(key).find((g) => g.group.startsWith('Each row'))?.fields) || [];

const sampleRows = (key?: string): PrintData[] => {
  const fields = rowFieldsFor(key);
  return Array.from({ length: SAMPLE_ROW_COUNT }, (_item, i) => {
    const row: PrintData = {};
    fields.forEach((f) => { row[f.token] = sampleValue(f.token, f.label, i + 1); });
    return row;
  });
};

export const sampleFor = (key?: string): PrintData => {
  switch (key) {
    case 'COMMUNION_CERTIFICATE':
      return {
        ...CHURCH_SAMPLE,
        communionRegisterNo: 'FC-12', communionNo: 'FC-0031', certificateNo: 'CERT-0091',
        personName: 'John Michael Samuel', baptismReference: 'Baptised 20 Apr 1986',
        communionDate: '15 Sep 2026', minister: 'Rev. Daniel Raj',
        church: 'CSI Christ Church, Devicode', remarks: '\u2014'
      };
    case 'CONFIRMATION_CERTIFICATE':
      return {
        ...CHURCH_SAMPLE,
        confirmationRegisterNo: 'CF-08', confirmationNo: 'CF-0017', certificateNo: 'CERT-0104',
        personName: 'John Michael Samuel', baptismReference: 'Baptised 20 Apr 1986',
        confirmationDate: '15 Sep 2026', bishopName: 'Rt. Rev. Samuel George',
        church: 'CSI Christ Church, Devicode', remarks: '\u2014'
      };
    case 'BANNS_NOTICE':
      return {
        ...CHURCH_SAMPLE,
        bannNo: 'BN-2026-003',
        groomName: 'John Michael Samuel', groomChurch: 'CSI Christ Church, Devicode',
        brideName: 'Anitha Grace John', brideChurch: 'CSI St. Peter\u2019s Church, Nagercoil',
        firstDate: '30 Aug 2026', secondDate: '06 Sep 2026', thirdDate: '13 Sep 2026',
        officiatingPresbyter: 'Rev. Daniel Raj', objections: 'None received',
        approvalStatus: 'Approved'
      };
    case 'BAPTISM_CERTIFICATE':
      return {
        ...CHURCH_SAMPLE,
        registerNo: 'REG-204', certificateNo: 'CERT-0091',
        childName: 'John Michael Samuel', dob: '04 Jun 2024',
        baptismDate: '15 Sep 2026', parentsNames: 'David Samuel & Anitha David',
        godparents: 'Thomas Varghese, Sarah Philip',
        minister: 'Rev. Daniel Raj', church: 'CSI Christ Church, Devicode',
        remarks: '—'
      };
    case 'MARRIAGE_CERTIFICATE':
      return {
        ...CHURCH_SAMPLE,
        registerNo: 'REG-118', certificateNo: 'MC-0042',
        groomName: 'John Michael Samuel', brideName: 'Anitha Grace John',
        parentName: 'David Samuel', marriageDate: '15 Sep 2026',
        marriagePlace: 'CSI Christ Church, Devicode', minister: 'Rev. Daniel Raj',
        witness1: 'Thomas Varghese', witness2: 'Sarah Philip',
        registrationDetails: 'Reg. 44/2026'
      };
    case 'MEMBER_RECORD':
      return {
        ...CHURCH_SAMPLE,
        memberId: 'MEM-1042', name: 'John Michael Samuel', relativeName: 'David Samuel',
        gender: 'Male', dateOfBirth: '12 Mar 1986', maritalStatus: 'Married',
        dateOfMarriage: '18 Jan 2012', bloodGroup: 'O+', education: 'B.Sc.',
        occupation: 'Teacher', mobileNumber: '9876543210', location: 'Devicode',
        familyId: 'FAM-118', relationToHead: 'Head', memberStatus: 'Communicant',
        membershipDate: '02 Feb 2005', baptismDate: '20 Apr 1986',
        confirmationDate: '11 Nov 1999'
      };
    case 'FAMILY_RECORD':
      return {
        ...CHURCH_SAMPLE,
        familyId: 'FAM-118', familyName: "Samuel's Family", membershipNo: '118',
        headOfFamily: 'John Michael Samuel', headGender: 'Male',
        parish: 'CSI Christ Church', contactNumber: '9876543210',
        email: 'john@example.com', address: '14 Church Street, Devicode',
        status: 'ACTIVE', rowCount: 3,
        rows: [
          { memberId: 'MEM-1042', name: 'John Michael Samuel', headOfFamily: 'John Michael Samuel', relationToHead: 'Head', gender: 'Male', dateOfBirth: '12 Mar 1986', mobileNumber: '9876543210', memberStatus: 'Communicant' },
          { memberId: 'MEM-1043', name: 'Anitha Grace John', headOfFamily: 'John Michael Samuel', relationToHead: 'Wife', gender: 'Female', dateOfBirth: '04 Aug 1989', mobileNumber: '9876543211', memberStatus: 'Communicant' },
          { memberId: 'MEM-1044', name: 'Grace Samuel', headOfFamily: 'John Michael Samuel', relationToHead: 'Daughter', gender: 'Female', dateOfBirth: '18 Jan 2014', mobileNumber: '', memberStatus: 'Baptised' }
        ]
      };
    case 'FAMILY_REGISTRATION':
      return {
        ...CHURCH_SAMPLE,
        referenceNo: 'REG-7K3M9Q2A', registrationStatus: 'Approved',
        submittedAt: '12 Sep 2026, 10:15 AM', approvedAt: '14 Sep 2026, 4:30 PM', rejectReason: '',
        familyId: 'FAM-118', membershipNo: '118', headOfFamily: 'John Michael Samuel',
        headMobile: '9876543210', headDateOfBirth: '12 Mar 1986',
        address: '14 Church Street, Devicode', parish: 'CSI Christ Church', email: 'john@example.com',
        memberCount: 3, rowCount: 3,
        rows: [
          { index: 1, name: 'John Michael Samuel', relationToHead: 'Head', gender: 'Male', dateOfBirth: '12 Mar 1986', maritalStatus: 'Married', mobileNumber: '9876543210', memberStatus: 'Regular', lifeStatus: 'Alive', baptismDate: '20 Apr 1986', communionStatus: 'Received', aadhaarNumber: 'XXXX XXXX 4321' },
          { index: 2, name: 'Anitha Grace John', relationToHead: 'Spouse', gender: 'Female', dateOfBirth: '04 Aug 1989', maritalStatus: 'Married', mobileNumber: '9876543211', memberStatus: 'Regular', lifeStatus: 'Alive', baptismDate: '10 Sep 1989', communionStatus: 'Received', aadhaarNumber: '' },
          { index: 3, name: 'Grace Samuel', relationToHead: 'Daughter', gender: 'Female', dateOfBirth: '18 Jan 2014', maritalStatus: 'Single', mobileNumber: '', memberStatus: 'Regular', lifeStatus: 'Alive', baptismDate: '02 Mar 2014', communionStatus: 'In Preparation', aadhaarNumber: '' }
        ]
      };
    case 'YOUTH_FELLOWSHIP_RECORD':
      return {
        ...CHURCH_SAMPLE,
        recordId: 'YFG-1003', name: 'Senior Youth', ageBand: '18 to 35', ageFrom: 18, ageTo: 35,
        gender: 'Both', memberCount: 3, rowCount: 3, notes: 'Meets on the second Sunday after the evening service.',
        createdBy: 'Secretary',
        rows: [
          { index: 1, name: 'Anitha Grace', memberId: 'MEM-1043', age: 24, gender: 'Female', dateOfBirth: '04 Aug 2002', mobileNumber: '9876543211', familyId: 'FAM-118', memberStatus: 'Regular' },
          { index: 2, name: 'David Samuel', memberId: 'MEM-1051', age: 29, gender: 'Male', dateOfBirth: '11 Feb 1997', mobileNumber: '9876543222', familyId: 'FAM-121', memberStatus: 'Regular' },
          { index: 3, name: 'Priya Joseph', memberId: 'MEM-1066', age: 19, gender: 'Female', dateOfBirth: '30 May 2007', mobileNumber: '', familyId: 'FAM-130', memberStatus: 'Associate' }
        ]
      };
    case 'YOUTH_PROGRAM_RECORD':
      return {
        ...CHURCH_SAMPLE,
        recordId: 'YPR-1012', title: 'Youth Retreat 2026', date: '14 Nov 2026', fellowshipName: 'Senior Youth',
        progType: 'Retreat', attendance: '42', budget: '₹18,500.00',
        notes: 'Two-day retreat at the diocesan camp centre. Theme: "Rooted in Christ".', createdBy: 'Secretary'
      };
    case 'MISSIONARY_PROFILE':
      return {
        ...CHURCH_SAMPLE,
        fullName: 'Sarah Philip', recordId: 'MSN-1007', dob: '09 Aug 1982',
        gender: 'Female', mobileNumber: '9876500011', email: 'sarah@example.com',
        missionField: 'North India', organization: 'Friends Missionary Prayer Band',
        ministryType: 'Church Planting', location: 'Bhopal, Madhya Pradesh',
        dateJoined: '01 Jun 2010'
      };
    case 'BURIAL_RECORD':
      return {
        ...CHURCH_SAMPLE,
        deceasedName: 'Samuel Thomas', dateOfDeath: '11 Sep 2026',
        date: '13 Sep 2026', assignedPlot: 'Section B — Plot 42',
        nextOfKin: 'John Michael Samuel', memberId: 'MEM-0880',
        notes: '—'
      };
    case 'OFFERING_RECEIPT':
      return {
        ...CHURCH_SAMPLE,
        receipt: 'RCT-2051', date: '24 Sep 2026', amount: '5,000.00',
        donorName: 'John Michael Samuel', purpose: 'Building Fund',
        source: 'Cash', givenBy: 'John Michael Samuel',
        countedBy: 'Mary Kurian', verifiedBy: 'Rev. Daniel Raj'
      };
    case 'REPORT_CARD':
      return {
        ...CHURCH_SAMPLE,
        studentName: 'Grace Samuel', className: 'Grade 3 — A',
        academicYear: '2026 – 2027',
        rows: [
          { index: 1, exam: 'Quarterly', subject: 'Bible Knowledge', date: '12 Jul 2026', total: 50, scored: 44, result: 'PASS' },
          { index: 2, exam: 'Quarterly', subject: 'Memory Verses', date: '12 Jul 2026', total: 50, scored: 39, result: 'PASS' },
          { index: 3, exam: 'Annual', subject: 'Bible Knowledge', date: '14 Mar 2027', total: 100, scored: 82, result: 'PASS' }
        ]
      };
    case 'FINANCIAL_STATEMENT':
      return {
        ...CHURCH_SAMPLE,
        title: 'Official Cash Book', reference: '01 Sep 2026 to 30 Sep 2026',
        rowCount: 3,
        rows: [
          { date: '02 Sep 2026', particulars: 'Sunday Offering', receipt: '12,400.00', payment: '', amount: '12,400.00', balance: '62,400.00' },
          { date: '09 Sep 2026', particulars: 'Electricity Bill', receipt: '', payment: '3,150.00', amount: '3,150.00', balance: '59,250.00' },
          { date: '16 Sep 2026', particulars: 'Harvest Festival', receipt: '48,900.00', payment: '', amount: '48,900.00', balance: '1,08,150.00' }
        ]
      };
    default: {
      // A record sheet with no hand-written sample still previews with a value
      // in every field it offers.
      const details: PrintData = {};
      detailFields(key).forEach((f) => { details[f.token] = sampleValue(f.token, f.label, 1); });
      return {
        ...CHURCH_SAMPLE,
        ...details,
        title: documentLabel(key), reference: 'September 2026', rowCount: SAMPLE_ROW_COUNT,
        rows: sampleRows(key)
      };
    }
  }
};

// ---------------------------------------------------------------------------
// The document's own content, which every design wraps
// ---------------------------------------------------------------------------

/**
 * How a document's content is laid out. A design styles all three, so one
 * design serves every document without knowing what any of them contain.
 */
export type BodyShape = 'prose' | 'details' | 'rows';

/**
 * The wording of a certificate is the document, so each is written out in its
 * own words using only the fields that certificate's register actually stores.
 * A marriage certificate cannot pick up `childName`, because it is not here.
 */
const CERTIFICATE_BODIES: Record<string, string> = {
  BAPTISM_CERTIFICATE: `<p class="lead">This is to certify that</p>
<p class="subject">{{childName}}</p>
<p class="line">born on <strong>{{dob}}</strong> to <strong>{{parentsNames}}</strong></p>
<p class="line">was baptised on <strong>{{baptismDate}}</strong></p>
<p class="line">at {{church}} by <strong>{{minister}}</strong>.</p>
<p class="line">Godparents: {{godparents}}</p>
<p class="ref">Register No. {{registerNo}} &nbsp;&middot;&nbsp; Certificate No. {{certificateNo}}</p>`,

  COMMUNION_CERTIFICATE: `<p class="lead">This is to certify that</p>
<p class="subject">{{personName}}</p>
<p class="line">{{baptismReference}}</p>
<p class="line">received First Holy Communion on <strong>{{communionDate}}</strong></p>
<p class="line">at {{church}} by <strong>{{minister}}</strong>.</p>
<p class="ref">Register No. {{communionRegisterNo}} &nbsp;&middot;&nbsp; Communion No. {{communionNo}} &nbsp;&middot;&nbsp; Certificate No. {{certificateNo}}</p>`,

  CONFIRMATION_CERTIFICATE: `<p class="lead">This is to certify that</p>
<p class="subject">{{personName}}</p>
<p class="line">{{baptismReference}}</p>
<p class="line">was confirmed on <strong>{{confirmationDate}}</strong></p>
<p class="line">at {{church}} by <strong>{{bishopName}}</strong>.</p>
<p class="ref">Register No. {{confirmationRegisterNo}} &nbsp;&middot;&nbsp; Confirmation No. {{confirmationNo}} &nbsp;&middot;&nbsp; Certificate No. {{certificateNo}}</p>`,

  MARRIAGE_CERTIFICATE: `<p class="lead">This is to certify that</p>
<p class="subject">{{groomName}}</p>
<p class="line">and</p>
<p class="subject">{{brideName}}</p>
<p class="line">were joined in holy matrimony on <strong>{{marriageDate}}</strong></p>
<p class="line">at {{marriagePlace}} by <strong>{{minister}}</strong>.</p>
<p class="line">Witnesses: {{witness1}} and {{witness2}}</p>
<p class="ref">Register No. {{registerNo}} &nbsp;&middot;&nbsp; Certificate No. {{certificateNo}} &nbsp;&middot;&nbsp; {{registrationDetails}}</p>`,

  BANNS_NOTICE: `<p class="lead">Notice of Banns of Marriage</p>
<p class="line">A marriage is intended between</p>
<p class="subject">{{groomName}}</p>
<p class="line">of {{groomChurch}}</p>
<p class="line">and</p>
<p class="subject">{{brideName}}</p>
<p class="line">of {{brideChurch}}</p>
<p class="line">Published on {{firstDate}}, {{secondDate}} and {{thirdDate}}</p>
<p class="line">by <strong>{{officiatingPresbyter}}</strong>.</p>
<p class="line">Objections: {{objections}}</p>
<p class="ref">Bann No. {{bannNo}} &nbsp;&middot;&nbsp; Status: {{approvalStatus}}</p>`
};

/** The document's own fields, minus the church identity and any repeating list. */
const detailFields = (key?: string): FieldChoice[] =>
  fieldsFor(key)
    .filter((g) => g.group !== 'Church' && g.group !== 'The list'
      && !g.group.startsWith('Each row') && !isSheetList(g.group))
    .flatMap((g) => g.fields);

/**
 * The repeating part of a record sheet: a family's members, or the exams on a
 * student's report card. Anything else in a sheet prints as label/value rows —
 * which is how the report card used to print one blank "Exam" line instead of
 * its table of results.
 */
const isSheetList = (group: string): boolean =>
  group.startsWith('Each member') || group.startsWith('Each exam');

/**
 * A record sheet that also lists people — a family and its members. The names
 * repeat, so they are a table under the details rather than more label/value
 * rows, and they fill from the same {{#rows}} section every register uses.
 */
const memberFieldsFor = (key?: string): FieldChoice[] =>
  (fieldsFor(key).find((g) => isSheetList(g.group))?.fields) || [];

/**
 * Columns that hold money or a count. They are marked `num` so every design
 * right-aligns them — a column of amounts reads down its decimal points, and
 * a name in the last column is no longer pushed to the right edge.
 */
const NUMERIC_TOKENS = new Set([
  'index', 'amount', 'receipt', 'payment', 'balance', 'debit', 'credit',
  'cost', 'totalCost', 'currentValue', 'purchaseCost', 'quantity',
  'budget', 'actual', 'variance', 'allotted', 'total', 'approxAmount',
  'memberCount', 'headcount', 'attendance', 'expected', 'usedPct',
  'm1', 'm2', 'm3', 'm4', 'm5', 'm6', 'm7', 'm8', 'm9', 'm10', 'm11', 'm12'
]);

/** The repeating table shared by registers and by record sheets that list people. */
const rowsTable = (fields: FieldChoice[], footer: string): string => {
  const cls = (f: FieldChoice) => (NUMERIC_TOKENS.has(f.token) ? ' class="num"' : '');
  const head = fields.map((f) => `<th${cls(f)}>${f.token === 'index' ? '#' : f.label}</th>`).join('');
  const cells = fields.map((f) => `      <td${cls(f)}>{{${f.token}}}</td>`).join('\n');
  // More than seven columns do not fit a portrait sheet at full size.
  return `<table class="rows${fields.length > 7 ? ' wide' : ''}">
  <thead>
    <tr>${head}</tr>
  </thead>
  <tbody>
    {{#rows}}
    <tr>
${cells}
    </tr>
    {{/rows}}
  </tbody>
</table>
${footer}`;
};

export const bodyShapeFor = (key?: string): BodyShape => {
  if (key && CERTIFICATE_BODIES[key]) return 'prose';
  return rowFieldsFor(key).length ? 'rows' : 'details';
};

/**
 * The content block, in semantic markup only. Every design styles the same
 * classes, which is how ten designs cover twenty-three documents without any
 * of them naming a field.
 */
export const documentBodyHtml = (key?: string): string => {
  const shape = bodyShapeFor(key);

  if (shape === 'prose') return CERTIFICATE_BODIES[key as string];

  if (shape === 'rows') {
    // A register can carry figures of its own (an opening balance, totals). A
    // "Totals" group prints under the table, anything else above it.
    const figures = (groups: FieldGroup[]) => {
      const lines = groups.flatMap((g) => g.fields)
        .map((f) => `  <tr><th>${f.label}</th><td>{{${f.token}}}</td></tr>`)
        .join('\n');
      return lines ? `<table class="details">\n  <tbody>\n${lines}\n  </tbody>\n</table>\n` : '';
    };
    const own = fieldsFor(key).filter((g) => g.group !== 'Church' && g.group !== 'The list'
      && g.group !== 'Period' && !g.group.startsWith('Each row') && !g.group.startsWith('Each member'));
    const before = figures(own.filter((g) => g.group !== 'Totals'));
    const after = figures(own.filter((g) => g.group === 'Totals'));
    return `<p class="ref">{{reference}}</p>
${before}${rowsTable(rowFieldsFor(key).slice(0, documentFor(key)?.listColumns || 7), `<p class="ref">{{rowCount}} entries</p>\n${after}`)}`;
  }

  const details = detailFields(key)
    .map((f) => `  <tr><th>${f.label}</th><td>{{${f.token}}}</td></tr>`)
    .join('\n');
  const sheet = `<table class="details">
  <tbody>
${details}
  </tbody>
</table>`;

  // A record sheet that also lists people: the family's own details first, then
  // everyone in the household as a table under them.
  const people = memberFieldsFor(key);
  if (!people.length) return sheet;
  return `${sheet}
<p class="ref">{{rowCount}} ${key === 'REPORT_CARD' ? 'exams' : 'members'}</p>
${rowsTable(people.slice(0, 7), '')}`;
};
